<template>
  <div class="tasks-list-view">
    <div v-if="isCompactLayout" class="tasks-mobile-list">
      <task-mobile-card
        v-for="task in tasks"
        :key="'m-' + task.id"
        :task="task"
        :can-assign-to="canAssignTo"
        :priority-options="priorityOptions"
        :place-tags="placeTags"
        :tag-busy="tagBusyId === task.id"
        :saving="savingId === task.id"
        :active="task.id === activeTaskId"
        @select="$emit('selectTask', $event)"
        @toggle-complete="toggleComplete"
        @set-assignee="onMobileSetAssignee"
        @set-priority="onMobileSetPriority"
        @set-client="onMobileSetClient"
        @set-due="onMobileSetDue"
        @apply-tag="applyListTag($event.task, $event.payload)"
        @remove-tag="removeListTag($event.task, $event.tag)"
        @update-tag="updateTagDefinition"
        @delete-tag="deleteTagDefinition"
      />
      <div v-if="!tasks.length" class="tasks-empty">
        {{ t("tasks-no_tasks_found") }}
      </div>
    </div>

    <div v-else class="tasks-fluid-wrap">
      <div class="tasks-fluid-list">
        <div
          v-for="task in tasks"
          :key="task.id"
          class="tasks-row"
          :class="{
            active: task.id === activeTaskId,
            done: !!task.completed_at || Number(task.archived) === 1,
          }"
          @click="onRowClick(task)"
        >
          <div class="task-row-main">
            <button
              type="button"
              class="task-complete-btn"
              :class="{ completed: !!task.completed_at }"
              :title="
                task.completed_at ? t('tasks-reopen') : t('tasks-complete')
              "
              :disabled="savingId === task.id"
              @click.stop="toggleComplete(task)"
            >
              <Icon icon-class="ui-check" size="2" />
            </button>

            <span class="task-name">{{ task.title }}</span>

            <div class="task-tags-inline" @click.stop>
              <answer-tags
                compact
                display="dots"
                :tags="task.tags || []"
                :suggestions="placeTags"
                :busy="tagBusyId === task.id"
                @apply="applyListTag(task, $event)"
                @remove="removeListTag(task, $event)"
                @update="updateTagDefinition"
                @delete="deleteTagDefinition"
              />
            </div>

            <kv-chip
              v-if="task.completed_at"
              size="small"
              density="extra-compact"
              rounded="pill"
              color="success"
            >
              {{ t("tasks-status_completed") }}
            </kv-chip>
            <kv-chip
              v-else-if="Number(task.archived) === 1"
              size="small"
              density="extra-compact"
              rounded="pill"
              color="muted"
            >
              {{ t("tasks-status_archived") }}
            </kv-chip>

            <kv-popup
              class="task-inline-control"
              placement="bottom right"
              min-width="220px"
              max-width="280px"
            >
              <template #trigger="{ toggle }">
                <button
                  type="button"
                  class="task-avatar-btn"
                  :title="task.assignee_name || t('tasks-col_assignee')"
                  :disabled="savingId === task.id"
                  @click.stop="toggle"
                >
                  <span v-if="task.assignee_name" class="task-avatar">
                    {{ assigneeInitials(task.assignee_name) }}
                  </span>
                  <span v-else class="task-avatar empty">
                    <Icon icon-class="ui-user-01" size="2" />
                  </span>
                </button>
              </template>
              <template #content="{ toggle }">
                <div class="task-hot-menu">
                  <button
                    type="button"
                    class="task-hot-item"
                    @click.stop.prevent="setAssignee(task, null, toggle)"
                  >
                    {{ t("tasks-none") }}
                  </button>
                  <button
                    v-for="user in canAssignTo"
                    :key="user.id"
                    type="button"
                    class="task-hot-item"
                    :class="{
                      active: String(user.id) === String(task.assignee_id),
                    }"
                    @click.stop.prevent="setAssignee(task, user, toggle)"
                  >
                    <span class="task-avatar mini">{{
                      assigneeInitials(user.name)
                    }}</span>
                    <span>{{ user.name }}</span>
                  </button>
                </div>
              </template>
            </kv-popup>

            <kv-popup
              class="task-inline-control"
              placement="bottom right"
              min-width="180px"
            >
              <template #trigger="{ toggle }">
                <button
                  type="button"
                  class="task-flag-btn"
                  :class="'tone-' + priorityTone(task)"
                  :title="priorityLabel(task)"
                  :aria-label="priorityLabel(task)"
                  :disabled="savingId === task.id"
                  @click.stop="toggle"
                >
                  <Icon icon-class="ui-flag-01" size="2" />
                </button>
              </template>
              <template #content="{ toggle }">
                <div class="task-hot-menu">
                  <button
                    v-for="opt in priorityOptions"
                    :key="opt.value"
                    type="button"
                    class="task-hot-item"
                    :class="'tone-' + opt.tone"
                    @click.stop.prevent="setPriority(task, opt.value, toggle)"
                  >
                    <Icon
                      :icon-class="
                        opt.value === 0 ? 'ui-x-close' : 'ui-flag-01'
                      "
                      size="3"
                    />
                    <span>{{ opt.label }}</span>
                  </button>
                </div>
              </template>
            </kv-popup>

            <kv-popup
              class="task-inline-control task-client-popup"
              placement="bottom right"
              min-width="420px"
              max-width="480px"
            >
              <template #trigger="{ toggle }">
                <button
                  type="button"
                  class="task-meta-pill"
                  :class="{ empty: !clientLabel(task) }"
                  :title="clientLabel(task) || t('tasks-col_client')"
                  :disabled="savingId === task.id"
                  @click.stop="toggle"
                >
                  <Icon icon-class="ui-user-01" size="2" />
                  <span>{{
                    clientLabel(task) || t("tasks-col_client")
                  }}</span>
                </button>
              </template>
              <template #content="{ toggle }">
                <div class="task-client-menu">
                  <div class="task-client-field" @click.stop>
                    <clients-field
                      :value="task.clients_id || ''"
                      @input="setClient(task, $event, toggle)"
                    />
                  </div>
                  <button
                    v-if="task.clients_id"
                    type="button"
                    class="task-hot-item"
                    @click.stop.prevent="setClient(task, null, toggle)"
                  >
                    {{ t("tasks-linked_answer_none") }}
                  </button>
                </div>
              </template>
            </kv-popup>

            <div class="task-due-cell task-inline-control" @click.stop>
              <kv-date-picker
                :value="dueDateInput(task) || null"
                value-format="yyyy-MM-dd"
                size="small"
                variant="tonal"
                placement="bottom right"
                :placeholder="t('tasks-no_date')"
                :disabled="savingId === task.id"
                :clearable="true"
                @input="setDueDate(task, $event || null)"
              >
                <template #trigger>
                  <button
                    type="button"
                    class="task-meta-pill task-due-pill"
                    :class="{
                      empty: !dueDateInput(task),
                      late: dueTone(task) === 'late',
                    }"
                    :disabled="savingId === task.id"
                    :title="t('tasks-col_due_date')"
                  >
                    <Icon icon-class="ui-calendar" size="2" />
                    <human-date
                      v-if="dueDateInput(task)"
                      :date="dueDateInput(task) + 'T12:00:00'"
                      :format="t('dates-format_weekday_date')"
                    />
                    <span v-else>{{ t("tasks-no_date") }}</span>
                  </button>
                </template>
              </kv-date-picker>
            </div>
          </div>

          <div class="task-row-aside" @click.stop>
            <kv-popup placement="bottom right" min-width="200px" max-width="280px">
              <template #trigger="{ toggle }">
                <button
                  type="button"
                  class="task-text-trigger"
                  :disabled="savingId === task.id"
                  @click.stop="toggle"
                >
                  <span :class="{ muted: !task.board_name }">{{
                    task.board_name || t("tasks-no_board_option")
                  }}</span>
                </button>
              </template>
              <template #content="{ toggle }">
                <div class="task-hot-menu">
                  <button
                    type="button"
                    class="task-hot-item"
                    @click.stop.prevent="setBoard(task, null, toggle)"
                  >
                    {{ t("tasks-none") }}
                  </button>
                  <button
                    v-for="board in boards"
                    :key="board.id"
                    type="button"
                    class="task-hot-item"
                    :class="{
                      active:
                        String(board.id) === String(task.tasks_boards_id),
                    }"
                    @click.stop.prevent="setBoard(task, board, toggle)"
                  >
                    {{ board.name }}
                  </button>
                </div>
              </template>
            </kv-popup>

            <kv-popup
              v-if="task.tasks_boards_id"
              placement="bottom left"
              min-width="200px"
              max-width="280px"
            >
              <template #trigger="{ toggle }">
                <button
                  type="button"
                  class="task-stage-trigger"
                  :disabled="savingId === task.id"
                  @click.stop="toggle"
                >
                  <span v-if="task.column_name" class="task-stage-pill">
                    {{ task.column_name }}
                  </span>
                  <span v-else class="muted">{{ t("tasks-none") }}</span>
                </button>
              </template>
              <template #content="{ toggle }">
                <div class="task-hot-menu">
                  <button
                    v-for="column in columnsForTask(task)"
                    :key="column.id"
                    type="button"
                    class="task-hot-item"
                    :class="{
                      active:
                        String(column.id) ===
                        String(task.tasks_boards_columns_id),
                    }"
                    @click.stop.prevent="setStage(task, column, toggle)"
                  >
                    {{ column.name }}
                  </button>
                  <div
                    v-if="!columnsForTask(task).length"
                    class="task-hot-item disabled"
                  >
                    {{ t("tasks-none") }}
                  </div>
                </div>
              </template>
            </kv-popup>
          </div>
        </div>
      </div>
      <div v-if="!tasks.length" class="tasks-empty">
        {{ t("tasks-no_tasks_found") }}
      </div>
    </div>

    <drawer
      ref="taskDrawer"
      id="task-details-drawer"
      size="xl"
      :has-header="false"
      :backdrop="false"
      @hide="onDrawerHide"
    >
      <template #body="{ close }">
        <Details
          v-if="activeTask"
          :active-task="activeTask"
          :active-task-id="activeTaskId"
          @changed="taskChanged"
          @closeModal="close"
          @selectTask="(id) => $emit('selectTask', id)"
        />
      </template>
    </drawer>
  </div>
</template>

<style scoped lang="scss">
.tasks-list-view {
  width: 100%;
}

.tasks-mobile-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.tasks-fluid-wrap {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
}

.tasks-fluid-list {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 720px;
}

.tasks-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 8px;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  transition: background-color 0.12s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f9fafb;

    .task-complete-btn:not(.completed) {
      color: #d2d6db;
      border-color: #d2d6db;
    }
  }

  &.active {
    background: #edfcf3;
  }

  &.done {
    opacity: 0.65;

    .task-name {
      color: #9da4ae;
      text-decoration: line-through;
    }
  }
}

.task-row-main {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1 1 auto;
}

.task-row-aside {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
  max-width: 280px;

  ::v-deep .kv-popup-container {
    max-width: 140px;
    min-width: 0;
  }

  ::v-deep .kv-popup-container > div:first-child {
    min-width: 0;
    max-width: 100%;
  }

  .task-text-trigger {
    max-width: 140px;
  }
}

::v-deep .task-inline-control.kv-popup-container {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.task-tags-inline {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
}

.task-name {
  flex: 0 1 auto;
  min-width: 0;
  max-width: min(480px, 40vw);
  font-size: 14px;
  font-weight: 500;
  color: #111927;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.task-complete-btn {
  box-sizing: border-box;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px dashed #e5e7eb;
  background: transparent;
  color: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    color: #9da4ae;
    border-color: #9da4ae;
  }

  &.completed {
    color: var(--primary, #0b9055);
    border-style: solid;
    border-color: #a6f4c5;
    background: #edfcf3;
  }

  &:disabled {
    opacity: 0.5;
    cursor: wait;
  }
}

.task-avatar-btn,
.task-text-trigger,
.task-due-trigger,
.task-stage-trigger,
.task-flag-btn {
  &:disabled {
    opacity: 0.5;
    cursor: wait;
  }
}

.task-avatar-btn {
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
}

.task-avatar {
  box-sizing: border-box;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #eef2ff;
  color: #3538cd;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  border: 1px solid transparent;

  &.empty {
    background: #fff;
    color: #9da4ae;
    border-color: #e5e7eb;
    border-style: dashed;
  }

  &.mini {
    width: 22px;
    height: 22px;
    font-size: 10px;
    flex-shrink: 0;
  }
}

.task-flag-btn {
  box-sizing: border-box;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;

  &.tone-high {
    color: #f04438;
    background: #fef3f2;
    border-color: transparent;
  }

  &.tone-medium {
    color: #dc6803;
    background: #fffaeb;
    border-color: transparent;
  }

  &.tone-low {
    color: #384250;
    background: #f3f4f6;
    border-color: transparent;
  }

  &.tone-undefined {
    color: #9da4ae;
    border-style: dashed;
    background: transparent;
  }
}

.task-hot-menu {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 280px;
  overflow: auto;
  min-width: 160px;
}

.task-hot-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: none;
  background: transparent;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  text-align: left;
  color: #384250;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
  }

  &.active {
    background: #edfcf3;
    color: #0b9055;
  }

  &.disabled {
    cursor: default;
    color: #9da4ae;
  }

  &.tone-high {
    color: #f04438;
  }
  &.tone-medium {
    color: #dc6803;
  }
  &.tone-low {
    color: #384250;
  }
  &.tone-undefined {
    color: #6c737f;
  }
}

.task-text-trigger {
  display: block;
  width: auto;
  max-width: 100%;
  min-width: 0;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  text-align: right;
  color: inherit;
  overflow: hidden;
  font-size: 13px;

  span {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.task-meta-pill {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid transparent;
  border-radius: 999px;
  background: #f3f4f6;
  color: #384250;
  font-size: 12px;
  font-weight: 400;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  max-width: 160px;
  min-width: 0;
  flex-shrink: 0;
  vertical-align: middle;

  span,
  ::v-deep .human-date {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &.empty {
    color: #9da4ae;
    background: #fff;
    border-color: #e5e7eb;
    border-style: dashed;
  }

  &.late {
    color: #f04438;
    background: #fef3f2;
    border-color: transparent;
  }

  &:disabled {
    opacity: 0.5;
    cursor: wait;
  }
}

.task-due-pill {
  max-width: 220px;
}

::v-deep .task-client-popup.kv-popup-container .kv-popup-content {
  z-index: 3000;
  background: #fff;
  overflow: visible;
}

.task-client-menu {
  width: 100%;
  min-width: 390px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #fff;
  position: relative;
  z-index: 1;
}

.task-client-field {
  display: block;
  width: 100%;
  position: relative;
  z-index: 5;
  background: #fff;

  ::v-deep .autocomplete {
    width: 100%;
    position: relative;
    z-index: 5;
    background: #fff;
  }

  ::v-deep .autocomplete-input {
    width: 100%;
    min-height: 44px;
    font-size: 14px;
    padding: 10px 12px;
    border-radius: 8px;
    background: #fff;
  }

  ::v-deep .autocomplete-result-list {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    width: 100%;
    min-height: 160px;
    max-height: 320px;
    overflow-y: auto;
    margin: 0;
    padding: 0;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #fff !important;
    box-shadow: 0 12px 32px rgba(16, 24, 40, 0.18);
    z-index: 4000 !important;

    li {
      margin: 0;
      padding: 12px 14px;
      font-size: 14px;
      line-height: 1.45;
      color: #111927;
      background: #fff;
      border-bottom: 1px solid #f3f4f6;

      &:last-child {
        border-bottom: none;
      }

      &:hover {
        background: #f9fafb;
      }
    }
  }
}

.task-due-cell {
  flex-shrink: 0;
  margin-left: 4px;

  ::v-deep .kv-date-picker,
  ::v-deep .kv-date-picker-trigger {
    display: inline-flex;
    align-items: center;
    width: auto;
  }
}

.task-stage-trigger {
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  max-width: 100%;

  &:disabled {
    cursor: default;
  }
}

.task-stage-pill {
  display: inline-block;
  max-width: 100%;
  padding: 4px 10px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #384250;
  font-size: 12px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.muted {
  color: #9da4ae;
}

.tasks-empty {
  padding: 32px 16px;
  text-align: center;
  color: #6c737f;
  font-size: 14px;
}

@media (max-width: 1100px) and (min-width: 768px) {
  .task-row-aside {
    max-width: 220px;
  }

  .task-meta-pill:not(.task-due-pill) {
    max-width: 120px;
  }
}

</style>

<script>
import { mapState } from "vuex";
import Icon from "../components/icon/icon";
import KvChip from "../components/ui/kv-chip.vue";
import KvPopup from "../components/ui/kv-popup.vue";
import KvDatePicker from "../components/inputs/kv-date-picker.vue";
import HumanDate from "../components/human-date/human-date";
import ClientsField from "../clients/clients-field";
import Details from "./task-details";
import Drawer from "../components/drawer/drawer.vue";
import TaskMobileCard from "./task-mobile-card.vue";
import AnswerTags from "../inbox/answer-tags.vue";
import keviApi from "../keviApi";
import { placeTodayDateString } from "../helpers/datetime";
import {
  taskAssigneeInitials,
  taskClientName,
  taskPriorityFlagTone,
  taskPriorityKey,
} from "../helpers/taskDisplay";
require("./task-drawer.less");

export default {
  components: {
    Icon,
    KvChip,
    Details,
    Drawer,
    HumanDate,
    "kv-popup": KvPopup,
    "kv-date-picker": KvDatePicker,
    "clients-field": ClientsField,
    "task-mobile-card": TaskMobileCard,
    "answer-tags": AnswerTags,
  },
  props: {
    tasks: Array,
    totalTasks: Number,
  },
  data() {
    return {
      activeTaskId: null,
      activeTask: null,
      savingId: null,
      boards: [],
      canAssignTo: [],
      isCompactLayout: false,
      placeTags: [],
      tagBusyId: null,
      ignoreRowClickUntil: 0,
      switchingTask: false,
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.user,
    }),
    ...mapState("placeDetails", ["users"]),
    placeId() {
      return this.$store.state.place.id;
    },
    priorityOptions() {
      return [
        {
          value: 1,
          tone: "low",
          label: this.t("tasks-priority_low"),
        },
        {
          value: 2,
          tone: "medium",
          label: this.t("tasks-priority_medium"),
        },
        {
          value: 3,
          tone: "high",
          label: this.t("tasks-priority_high"),
        },
        {
          value: 0,
          tone: "undefined",
          label: this.t("tasks-priority_undefined"),
        },
      ];
    },
  },
  mounted() {
    this.updateCompactLayout();
    this.loadAssignableUsers();
    this.loadBoards();
    this.loadPlaceTags();
    window.addEventListener("keydown", this.handleTaskKeydown);
    window.addEventListener("resize", this.updateCompactLayout);
    document.addEventListener("mousedown", this.onDocumentMouseDown, true);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleTaskKeydown);
    window.removeEventListener("resize", this.updateCompactLayout);
    document.removeEventListener("mousedown", this.onDocumentMouseDown, true);
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    updateCompactLayout() {
      this.isCompactLayout = window.innerWidth <= 767;
    },
    onMobileSetAssignee({ task, user, toggle }) {
      this.setAssignee(task, user, toggle);
    },
    onMobileSetPriority({ task, value, toggle }) {
      this.setPriority(task, value, toggle);
    },
    onMobileSetClient({ task, value, toggle }) {
      this.setClient(task, value, toggle);
    },
    onMobileSetDue({ task, value }) {
      this.setDueDate(task, value);
    },
    isTypingTarget(target) {
      if (!target) {
        return false;
      }
      const tag = (target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") {
        return true;
      }
      return !!target.isContentEditable;
    },
    handleTaskKeydown(event) {
      if (!this.activeTaskId || this.isTypingTarget(event.target)) {
        return;
      }
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
        return;
      }
      const index = (this.tasks || []).findIndex(
        (task) => String(task.id) === String(this.activeTaskId),
      );
      if (index < 0) {
        return;
      }
      const nextIndex = event.key === "ArrowDown" ? index + 1 : index - 1;
      const nextTask = (this.tasks || [])[nextIndex];
      if (!nextTask) {
        return;
      }
      event.preventDefault();
      this.$emit("selectTask", nextTask.id);
    },
    clientLabel: taskClientName,
    assigneeInitials: taskAssigneeInitials,
    priorityTone(task) {
      return taskPriorityFlagTone(task.priority);
    },
    priorityLabel(task) {
      const key = taskPriorityKey(task.priority);
      if (key === "medium") {
        return this.t("tasks-priority_medium");
      }
      if (key === "high") {
        return this.t("tasks-priority_high");
      }
      if (key === "low") {
        return this.t("tasks-priority_low");
      }
      return this.t("tasks-priority_undefined");
    },
    dueTone(task) {
      if (!task.completed_at && task.due_date) {
        const today = placeTodayDateString(this.$store.state.place.timezone);
        const dueDay = String(task.due_date).slice(0, 10);
        if (dueDay < today) {
          return "late";
        }
        if (dueDay === today) {
          return "today";
        }
      }
      return "";
    },
    dueDateInput(task) {
      if (!task || !task.due_date) {
        return "";
      }
      return String(task.due_date).slice(0, 10);
    },
    closePopup(toggle) {
      if (typeof toggle === "function") {
        toggle(false);
      }
    },
    columnsForTask(task) {
      if (!task || !task.tasks_boards_id) {
        return [];
      }
      const board = this.boards.find(
        (b) => String(b.id) === String(task.tasks_boards_id),
      );
      const columns = (board && board.columns) || [];
      return columns
        .slice()
        .sort((a, b) => Number(a.position || 0) - Number(b.position || 0));
    },
    boardMeta(boardId, columnId) {
      const board = this.boards.find((b) => String(b.id) === String(boardId));
      if (!board) {
        return {
          tasks_boards_id: boardId || null,
          board_name: null,
          tasks_boards_columns_id: null,
          column_name: null,
        };
      }
      const columns = (board.columns || [])
        .slice()
        .sort((a, b) => Number(a.position || 0) - Number(b.position || 0));
      const column =
        columns.find((c) => String(c.id) === String(columnId)) ||
        columns[0] ||
        null;
      return {
        tasks_boards_id: board.id,
        board_name: board.name,
        tasks_boards_columns_id: column ? column.id : null,
        column_name: column ? column.name : null,
      };
    },
    async loadAssignableUsers() {
      const level =
        (this.$store.state.user.permissions.tasks &&
          this.$store.state.user.permissions.tasks.options &&
          this.$store.state.user.permissions.tasks.options.level) ||
        "mine";
      await this.$store.dispatch("placeDetails/setPlace", this.placeId);
      await this.$nextTick();
      const users =
        this.users ||
        (this.$store.state.placeDetails && this.$store.state.placeDetails.users) ||
        [];
      const me = users.find((u) => u.username === this.user.username);
      if (level === "mine") {
        this.canAssignTo = me ? [{ ...me }] : [];
        return;
      }
      let filtered = users.filter((u) => parseInt(u.active, 10) === 1);
      if (level === "branch" && me) {
        filtered = filtered.filter(
          (u) => u.places_branches_id === me.places_branches_id,
        );
      }
      this.canAssignTo = filtered;
    },
    async loadBoards() {
      try {
        const options = keviApi.getOptions("/task-boards-columns", "GET");
        const response = await this.$http(options);
        this.boards = response.body || response.data || [];
      } catch (e) {
        this.boards = [];
      }
    },
    taskChanged({ id, task, moveNext }) {
      if (String(id) === String(this.activeTaskId)) {
        this.activeTask = { ...this.activeTask, ...task };
      }
      if (task && task.tags && Array.isArray(task.tags)) {
        (task.tags || []).forEach((tag) => this.rememberCreatedTag(tag));
      }
      this.$emit("changed", { id, task, moveNext });
    },
    async loadPlaceTags() {
      try {
        const response = await this.$http(keviApi.getOptions("tasks-tags"));
        this.placeTags = (response.body && response.body.tags) || [];
      } catch (error) {
        console.error("Error loading task tags", error);
      }
    },
    rememberCreatedTag(tag) {
      if (!tag || !tag.id) return;
      const exists = this.placeTags.some(
        (item) => String(item.id) === String(tag.id),
      );
      if (!exists) {
        this.placeTags = this.placeTags.concat([tag]);
      }
    },
    applyListTag(task, payload) {
      this.tagBusyId = task.id;
      const options = keviApi.getOptions(
        "task/" + task.id + "/tags",
        "POST",
        payload,
      );
      this.$http(options)
        .then((response) => {
          const body = response.body || {};
          this.applyLocalChange(task, { tags: body.tags || [] });
          if (body.tag) {
            this.rememberCreatedTag(body.tag);
          }
        })
        .catch(() => {
          this.$store.dispatch(
            "notification/error",
            this.t("tasks-notify_tag_error"),
          );
        })
        .finally(() => {
          this.tagBusyId = null;
        });
    },
    removeListTag(task, tag) {
      this.tagBusyId = task.id;
      const options = keviApi.getOptions(
        "task/" + task.id + "/tags/" + tag.id,
        "DELETE",
      );
      this.$http(options)
        .then((response) => {
          this.applyLocalChange(task, {
            tags: (response.body && response.body.tags) || [],
          });
        })
        .catch(() => {
          this.$store.dispatch(
            "notification/error",
            this.t("tasks-notify_tag_error"),
          );
        })
        .finally(() => {
          this.tagBusyId = null;
        });
    },
    patchTagAcrossTasks(tag) {
      if (!tag || !tag.id) return;
      this.placeTags = (this.placeTags || []).map((item) =>
        String(item.id) === String(tag.id) ? { ...item, ...tag } : item,
      );
      (this.tasks || []).forEach((task) => {
        const tags = task.tags || [];
        if (!tags.some((item) => String(item.id) === String(tag.id))) {
          return;
        }
        this.applyLocalChange(task, {
          tags: tags.map((item) =>
            String(item.id) === String(tag.id) ? { ...item, ...tag } : item,
          ),
        });
      });
    },
    removeTagAcrossTasks(tagId) {
      this.placeTags = (this.placeTags || []).filter(
        (item) => String(item.id) !== String(tagId),
      );
      (this.tasks || []).forEach((task) => {
        const tags = task.tags || [];
        if (!tags.some((item) => String(item.id) === String(tagId))) {
          return;
        }
        this.applyLocalChange(task, {
          tags: tags.filter((item) => String(item.id) !== String(tagId)),
        });
      });
    },
    updateTagDefinition(payload) {
      if (!payload || !payload.id) return;
      this.tagBusyId = payload.id;
      const options = keviApi.getOptions("tasks-tags", "POST", {
        id: payload.id,
        name: payload.name,
        color: payload.color,
      });
      this.$http(options)
        .then((response) => {
          const tag = (response.body && response.body.tag) || payload;
          this.patchTagAcrossTasks(tag);
        })
        .catch(() => {
          this.$store.dispatch(
            "notification/error",
            this.t("tasks-notify_tag_error"),
          );
        })
        .finally(() => {
          this.tagBusyId = null;
        });
    },
    deleteTagDefinition(tag) {
      if (!tag || !tag.id) return;
      this.tagBusyId = tag.id;
      const options = keviApi.getOptions("tasks-tags/" + tag.id, "DELETE");
      this.$http(options)
        .then(() => {
          this.removeTagAcrossTasks(tag.id);
        })
        .catch(() => {
          this.$store.dispatch(
            "notification/error",
            this.t("tasks-notify_tag_error"),
          );
        })
        .finally(() => {
          this.tagBusyId = null;
        });
    },
    applyLocalChange(task, patch) {
      this.$emit("changed", {
        id: task.id,
        task: patch,
        moveNext: false,
      });
      if (
        this.activeTask &&
        String(this.activeTaskId) === String(task.id)
      ) {
        this.activeTask = { ...this.activeTask, ...patch };
      }
    },
    suppressRowClick() {
      this.ignoreRowClickUntil = Date.now() + 400;
    },
    onRowClick(task) {
      if (Date.now() < this.ignoreRowClickUntil) {
        return;
      }
      this.$emit("selectTask", task.id);
    },
    async editTaskFields(task, fields = {}) {
      if (!task || this.savingId === task.id) {
        return null;
      }
      this.savingId = task.id;
      try {
        const payload = {
          assignee_id: Object.prototype.hasOwnProperty.call(fields, "assignee_id")
            ? fields.assignee_id || ""
            : task.assignee_id || "",
          clients_id: Object.prototype.hasOwnProperty.call(fields, "clients_id")
            ? fields.clients_id || ""
            : task.clients_id || "",
          due_date: Object.prototype.hasOwnProperty.call(fields, "due_date")
            ? fields.due_date || ""
            : task.due_date
              ? String(task.due_date).slice(0, 10)
              : "",
          priority: Object.prototype.hasOwnProperty.call(fields, "priority")
            ? Number(fields.priority)
            : Number(task.priority || 0),
        };
        if (Object.prototype.hasOwnProperty.call(fields, "tasks_boards_id")) {
          payload.tasks_boards_id = fields.tasks_boards_id;
        }
        const options = keviApi.getOptions(
          `task/${task.id}/edit`,
          "POST",
          payload,
        );
        const response = await this.$http(options);
        const updated =
          (response && response.body && response.body.task) ||
          (response && response.data && response.data.task) ||
          null;
        if (!updated) {
          throw new Error("missing task in edit response");
        }
        return updated;
      } catch (e) {
        console.error("editTaskFields failed", e);
        this.$store.dispatch(
          "notification/error",
          this.t("tasks-notify_error_update"),
        );
        return null;
      } finally {
        this.savingId = null;
      }
    },
    async toggleComplete(task) {
      if (!task || this.savingId === task.id) {
        return;
      }
      this.savingId = task.id;
      const action = task.completed_at ? "uncomplete" : "complete";
      try {
        const options = keviApi.getOptions(`task/${task.id}/${action}`, "POST");
        const response = await this.$http(options);
        const updated = response.body.task || {};
        this.applyLocalChange(task, {
          completed_at: updated.completed_at || null,
        });
        if (action === "complete") {
          this.$store.dispatch("notification/success", {
            text: this.t("tasks-notify_task_completed"),
            actionLabel: this.t("undo"),
            onAction: () => this.toggleComplete({ ...task, completed_at: updated.completed_at || new Date().toISOString() }),
          });
        } else {
          this.$store.dispatch(
            "notification/success",
            this.t("tasks-notify_task_reopened"),
          );
        }
      } catch (e) {
        this.$store.dispatch(
          "notification/error",
          this.t("tasks-notify_error_update"),
        );
      } finally {
        this.savingId = null;
      }
    },
    async setPriority(task, priority, toggle) {
      this.suppressRowClick();
      this.closePopup(toggle);
      if (!task || Number(task.priority) === Number(priority)) {
        return;
      }
      // Optimistic UI — API edit() is reliable but response joins can lag
      this.applyLocalChange(task, { priority: Number(priority) });
      const updated = await this.editTaskFields(task, { priority });
      if (!updated) {
        return;
      }
      this.applyLocalChange(task, {
        priority: Number(updated.priority),
      });
    },
    async setAssignee(task, user, toggle) {
      this.suppressRowClick();
      this.closePopup(toggle);
      const nextId = user ? user.id : null;
      if (String(task.assignee_id || "") === String(nextId || "")) {
        return;
      }
      this.applyLocalChange(task, {
        assignee_id: nextId,
        assignee_name: user ? user.name : null,
      });
      const updated = await this.editTaskFields(task, {
        assignee_id: nextId || "",
      });
      if (!updated) {
        return;
      }
      this.applyLocalChange(task, {
        assignee_id: updated.assignee_id || nextId,
        assignee_name:
          updated.assignee_name || (user ? user.name : null),
      });
    },
    async setClient(task, clientsId, toggle) {
      this.suppressRowClick();
      this.closePopup(toggle);
      const nextId = clientsId || null;
      if (String(task.clients_id || "") === String(nextId || "")) {
        return;
      }
      const updated = await this.editTaskFields(task, {
        clients_id: nextId || "",
      });
      if (!updated) {
        return;
      }
      let client = null;
      let clientName = null;
      if (nextId) {
        try {
          const options = keviApi.getOptions("place/client/" + nextId, "GET");
          const response = await this.$http(options);
          client = response.body;
          clientName = [client.first_name, client.last_name]
            .filter(Boolean)
            .join(" ")
            .trim();
        } catch (e) {
          client = { id: nextId };
        }
      }
      this.applyLocalChange(task, {
        clients_id: nextId,
        client,
        client_name: clientName,
      });
    },
    async setDueDate(task, value, toggle) {
      this.suppressRowClick();
      this.closePopup(toggle);
      const next =
        value == null || value === ""
          ? null
          : String(value).slice(0, 10);
      const current = task.due_date ? String(task.due_date).slice(0, 10) : null;
      if (next === current) {
        return;
      }
      this.applyLocalChange(task, { due_date: next });
      const updated = await this.editTaskFields(task, {
        due_date: next || "",
      });
      if (!updated) {
        return;
      }
      this.applyLocalChange(task, {
        due_date: updated.due_date ? String(updated.due_date).slice(0, 10) : next,
      });
    },
    async setBoard(task, board, toggle) {
      this.suppressRowClick();
      this.closePopup(toggle);
      const nextId = board ? board.id : "";
      if (String(task.tasks_boards_id || "") === String(nextId || "")) {
        return;
      }
      const updated = await this.editTaskFields(task, {
        tasks_boards_id: nextId,
      });
      if (!updated) {
        return;
      }
      if (!board) {
        this.applyLocalChange(task, {
          tasks_boards_id: null,
          board_name: null,
          tasks_boards_columns_id: null,
          column_name: null,
        });
        return;
      }
      const meta = this.boardMeta(
        board.id,
        updated.tasks_boards_columns_id,
      );
      this.applyLocalChange(task, meta);
    },
    async setStage(task, column, toggle) {
      this.suppressRowClick();
      this.closePopup(toggle);
      if (
        !task ||
        !task.tasks_boards_id ||
        !column ||
        String(task.tasks_boards_columns_id) === String(column.id)
      ) {
        return;
      }
      this.savingId = task.id;
      try {
        const options = keviApi.getOptions(
          `/task-boards/${task.tasks_boards_id}/tasks/${task.id}/move`,
          "POST",
          {
            tasks_boards_columns_id: column.id,
            position: 0,
          },
        );
        await this.$http(options);
        this.applyLocalChange(task, {
          tasks_boards_columns_id: column.id,
          column_name: column.name,
        });
      } catch (e) {
        this.$store.dispatch(
          "notification/error",
          this.t("tasks-notify_error_update"),
        );
      } finally {
        this.savingId = null;
      }
    },
    loadActiveTask() {
      if (!this.activeTaskId) {
        return;
      }
      const options = keviApi.getOptions("task/" + this.activeTaskId, "GET");
      this.$http(options).then((response) => {
        this.activeTask = response.body.task;
        this.$nextTick(() => {
          if (this.$refs.taskDrawer) {
            this.$refs.taskDrawer.open();
          }
        });
      });
    },
    onDrawerHide() {
      if (this.switchingTask) {
        return;
      }
      this.activeTask = null;
      if (this.activeTaskId) {
        this.activeTaskId = null;
        this.$emit("selectTask", null);
      }
    },
    onDocumentMouseDown(event) {
      const drawer = this.$refs.taskDrawer;
      if (!drawer || !drawer.isOpen) {
        return;
      }
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (target.closest(".drawer-panel")) {
        return;
      }
      if (
        target.closest(".tasks-row") ||
        target.closest(".tasks-mobile-list > *") ||
        target.closest(".task-mobile-card")
      ) {
        this.switchingTask = true;
        this.$nextTick(() => {
          this.switchingTask = false;
        });
        return;
      }
      if (target.closest(".kv-popup-content, .kv-dropdown, .task-hot-menu")) {
        return;
      }
      drawer.close();
    },
  },
  watch: {
    "$route.query": {
      immediate: true,
      handler() {
        const taskId = this.$route.query["task_id"];
        if (taskId && String(this.activeTaskId) !== String(taskId)) {
          this.switchingTask = true;
          this.activeTaskId = taskId;
          this.loadActiveTask();
          this.$nextTick(() => {
            this.switchingTask = false;
          });
          return;
        }
        if (!taskId && this.activeTaskId) {
          this.activeTaskId = null;
          this.activeTask = null;
          if (this.$refs.taskDrawer && this.$refs.taskDrawer.isOpen) {
            this.$refs.taskDrawer.close();
          }
        }
      },
    },
  },
};
</script>
